import { nullSafeToString, intToFormattedString, toDisplayPrice } from '../lib/util';
import {
    decorationsOf,
    orderedDecorationProofsOf,
    decorationChargesOf,
    decorationGroupIdOf,
    decorationGroupOf,
    isDecorationGroupFolded,
} from '../OrderItem/orderItemDecorations';

function toUserChoices (choices) {
    /*
     * A user choice with displayed=false is a costing or routing input the shopper never picked
     * (imposition, press selection), and costingOnly ones exist purely to drive price. Neither belongs
     * on a receipt, so only what admin marks displayable survives. sequence is the order admin
     * configured the fields in - the API does not pre-sort by it.
     */
    return Object.freeze((choices || [])
        .filter(choice => choice.displayed && !choice.costingOnly)
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map(choice => Object.freeze({
            label: nullSafeToString(choice.label || choice.name),
            value: nullSafeToString(choice.value),
        })));
}

function toDate (value) {
    return value ? Object.freeze(new Date(value)) : null;
}

/**
 * A single job on a submitted {@link Order Order}.
 *
 * Not to be confused with {@link OrderItem OrderItem}, which is a line in a {@link Project Project} -
 * a configured item that has not been ordered yet. Once a project is submitted its lines become these,
 * and they pick up production, proofing, shipping and return state that a project line has no concept
 * of. The two are separate models because the API returns genuinely different shapes for them.
 *
 * @property {number} id - (Read only) This item's unique id.
 * @property {number} orderId - (Read only) The id of the {@link Order Order} this item belongs to.
 * @property {string} name - (Read only) The item's name, falling back to the product name when the job was never named.
 * @property {string} jobName - (Read only) The name given to this job.
 * @property {?number} jobNumber - (Read only) The customer-facing job number. Null before one is assigned.
 * @property {string} jobDescription - (Read only) The description of this job.
 * @property {string} jobNotes - (Read only) Notes recorded against this job.
 * @property {string} productId - (Read only) The product id this item was ordered from.
 * @property {string} sku - (Read only) The SKU for this item, when the product carries one.
 * @property {string} externalId - (Read only) This item's id in an external system, if it came from one.
 * @property {number} quantity - (Read only) The quantity ordered.
 * @property {string} displayQuantity - (Read only) this.quantity as a string, formatted with commas.
 * @property {number} adjustedQuantity - (Read only) The quantity after any post-order adjustment. Equal to this.quantity when nothing was adjusted.
 * @property {string} displayAdjustedQuantity - (Read only) this.adjustedQuantity as a string, formatted with commas.
 * @property {number} price - (Read only) The undiscounted price of this item.
 * @property {string} displayPrice - (Read only) this.price formatted with commas and two decimal places.
 * @property {number} totalPrice - (Read only) The price charged for this item.
 * @property {string} displayTotalPrice - (Read only) this.totalPrice formatted with commas and two decimal places.
 * @property {number} totalPriceAfterAdjustments - (Read only) this.totalPrice once post-order adjustments are applied.
 * @property {string} displayTotalPriceAfterAdjustments - (Read only) this.totalPriceAfterAdjustments formatted with commas and two decimal places.
 * @property {number} handlingPrice - (Read only) Handling charged on this item.
 * @property {string} displayHandlingPrice - (Read only) this.handlingPrice formatted with commas and two decimal places.
 * @property {string} productionStatus - (Read only) Where this item is in production.
 * @property {string} proofStatus - (Read only) The proofing status of this item.
 * @property {?number} proofId - (Read only) The id of the proof configured for this item. Null when the product is not proofed, which is what tells you an approve/decline prompt does not belong on this item at all.
 * @property {?string} proofName - (Read only) The name of the proof for this item. Null if not applicable.
 * @property {string} proofDescription - (Read only) What this proof covers, as admin wrote it. Empty if not applicable.
 * @property {string} proofTerms - (Read only) The proofing terms the shopper is agreeing to when they approve. Show these before calling {@link Collaterate.Order.approveProof approveProof}. Empty if not applicable.
 * @property {OrderedItem~denialReason[]} proofDenialReasons - (Read only) The reasons this item's proof may be declined for - the list to build a picker from, not a record of why one was. Pass the chosen id to {@link Collaterate.Order.declineProof declineProof}. Empty when the product is not proofed.
 * @property {boolean} hasActivePrintFiles - (Read only) Whether this job currently has any active artwork. See {@link Collaterate.Order.getPrintFiles getPrintFiles}.
 * @property {string} serviceType - (Read only) The offering service type for this item.
 * @property {?Date} orderedOn - (Read only) When this item was ordered.
 * @property {?Date} estimatedShipBy - (Read only) The estimated ship-by date.
 * @property {?Date} shipBy - (Read only) The committed ship-by date.
 * @property {?Date} shippedOn - (Read only) When this item shipped. Null if it has not shipped.
 * @property {boolean} isShipped - (Read only) Whether this item has shipped.
 * @property {boolean} isShippable - (Read only) Whether this item ships at all, as opposed to being picked up or delivered digitally.
 * @property {boolean} isDigital - (Read only) Whether this is a digital item.
 * @property {boolean} isCancelled - (Read only) Whether this item was cancelled.
 * @property {boolean} isProductInvalid - (Read only) Whether the product behind this item is no longer valid.
 * @property {boolean} canReorder - (Read only) Whether this item can be reordered.
 * @property {?string} stockVariantDetails - (Read only) Variant details for a stock item. Null if not applicable.
 * @property {number} backorderedQuantity - (Read only) How much of a stock item is on backorder.
 * @property {?number} companionSourceId - (Read only) When this item was auto-added as a companion of another item (e.g. envelopes for a card), the id of that source item. Null otherwise.
 * @property {boolean} isCompanion - (Read only) Whether this item was auto-added as a companion of another item.
 * @property {OrderedItem~configDetail[]} configDetails - (Read only) The shopper-visible configuration choices for this item, in the order admin configured them. Empty if the product has none.
 * @property {OrderedItem~artworkFile[]} storedArtworkFiles - (Read only) Stored artwork associated with this item. Empty if there is none.
 * @property {boolean} canReturn - (Read only) Whether this item is eligible to be returned.
 * @property {?string} returnUrl - (Read only) The url that starts a return for this item. Null when it cannot be returned.
 * @property {number} quantityReturned - (Read only) How many units of this item have been returned.
 * @property {?string} returnStatus - (Read only) "RETURNED" or "PARTIALLY_RETURNED". Null if nothing was returned.
 * @property {?string} returnStatusLabel - (Read only) A display label for this.returnStatus. Null if nothing was returned.
 * @property {?string} returnStatusUrl - (Read only) The url showing the status of this item's return. Null if nothing was returned.
 * @property {Object[]} decorations - (Read only) Each decorated location as it was ordered, shaped like {@link OrderItem OrderItem}'s decorations, with the logo printed there as the order kept it at checkout. Empty for an undecorated item.
 * @property {Object[]} decorationProofs - (Read only) The item's proofs as the cart showed them at checkout, one per photo, shaped like {@link OrderItem OrderItem}'s decorationProofs. `image` is null until the kept copy has been rendered, shortly after checkout; show `photo` until then. Empty for an undecorated item, or one ordered before proofs were kept.
 * @property {Object[]} decorationCharges - (Read only) The decoration charges included in totalPrice, as charged at checkout, shaped like {@link OrderItem OrderItem}'s decorationCharges.
 * @property {?string} decorationGroupId - (Read only) Shared by the lines ordered together from a product's size grid with the same decorations, which were priced as one decorated order. Null for a line on its own.
 * @property {?Object} decorationGroup - (Read only) On a decorated group's first line, the group as one block, shaped like {@link OrderItem OrderItem}'s decorationGroup. Null on any other line.
 * @property {boolean} decorationGroupFolded - (Read only) True on a decorated group's later lines, which its first line's decorationGroup shows; list the group once by skipping these.
 */

class OrderedItem {
    constructor (data = {}) {
        this.id = data.id;
        this.orderId = data.orderId;

        this.name = nullSafeToString(data.jobNameOrProductName || data.name);
        this.jobName = nullSafeToString(data.jobName);
        this.jobNumber = data.jobNumber || null;
        this.jobDescription = nullSafeToString(data.jobDescription);
        this.jobNotes = nullSafeToString(data.jobNotes);

        this.productId = nullSafeToString(data.productId);
        this.sku = nullSafeToString(data.sku);
        this.externalId = nullSafeToString(data.externalId);

        this.quantity = data.orderedQuantity || 0;
        this.displayQuantity = intToFormattedString(this.quantity);
        /*
         * totalAdjustedQuantity is only populated once something adjusts it, so it falls back to the
         * ordered quantity - an unadjusted item would otherwise report zero.
         */
        this.adjustedQuantity = data.totalAdjustedQuantity || this.quantity;
        this.displayAdjustedQuantity = intToFormattedString(this.adjustedQuantity);

        this.price = data.price || 0;
        this.displayPrice = toDisplayPrice(this.price);
        this.totalPrice = data.totalPrice || 0;
        this.displayTotalPrice = toDisplayPrice(this.totalPrice);
        this.totalPriceAfterAdjustments = data.totalPriceAfterAdjustments || 0;
        this.displayTotalPriceAfterAdjustments = toDisplayPrice(this.totalPriceAfterAdjustments);
        this.handlingPrice = data.handlingPrice || 0;
        this.displayHandlingPrice = toDisplayPrice(this.handlingPrice);

        this.productionStatus = nullSafeToString(data.productionStatus);
        this.proofStatus = nullSafeToString(data.proofStatus);
        this.proofId = data.proofId || null;
        this.proofName = data.proofName || null;
        this.proofDescription = nullSafeToString(data.proofDescription);
        this.proofTerms = nullSafeToString(data.proofTerms);
        /*
         * Supplier#denialReasons reached through the item's proof - the options a shopper may pick from
         * when declining, not a history of past declines. A decline is recorded as an activity log entry
         * (the reason name and the shopper's comment, joined), so getActivityLog is where the record of
         * one actually lives.
         */
        this.proofDenialReasons = Object.freeze((data.denialReasons || []).map(reason => Object.freeze({
            id: reason.id,
            name: nullSafeToString(reason.name),
        })));
        this.hasActivePrintFiles = Boolean(data.activePrintFiles);
        this.serviceType = nullSafeToString(data.offeringServiceType);

        this.orderedOn = toDate(data.orderedOn);
        this.estimatedShipBy = toDate(data.estimatedShipBy);
        this.shipBy = toDate(data.shipBy);
        this.shippedOn = toDate(data.shippedOn);

        this.isShipped = Boolean(data.shipped);
        this.isShippable = Boolean(data.shippable);
        this.isDigital = Boolean(data.digital);
        this.isCancelled = Boolean(data.cancelled);
        this.isProductInvalid = Boolean(data.productInvalid);
        this.canReorder = Boolean(data.reorderEligible);

        this.stockVariantDetails = data.stockVariantDetails || null;
        this.backorderedQuantity = data.stockBackorderQuantity || 0;

        this.companionSourceId = data.companionSourceId || null;
        this.isCompanion = Boolean(data.companionSourceId);

        /*
         * Standard and large format products keep their choices in separate collections and an item is
         * only ever one or the other, so the two are concatenated rather than merged.
         */
        this.configDetails = Object.freeze([
            ...toUserChoices(data.orderItemPrintJobClassificationUserChoices),
            ...toUserChoices(data.orderItemLargeFormatPrintJobClassificationUserChoices),
        ]);

        this.storedArtworkFiles = Object.freeze((data.storedArtworkFiles || []).map(file => Object.freeze({
            id: file.id,
            idWithPrefix: nullSafeToString(file.idWithPrefix),
            name: nullSafeToString(file.name),
            link: nullSafeToString(file.link),
            downloadUrl: nullSafeToString(file.downloadUrl),
        })));

        this.canReturn = Boolean(data.returnEligible);
        this.returnUrl = data.returnUrl || null;
        this.quantityReturned = data.quantityReturned || 0;
        this.returnStatus = data.returnStatus || null;
        this.returnStatusLabel = data.returnStatusLabel || null;
        this.returnStatusUrl = data.returnStatusUrl || null;

        this.decorations = decorationsOf(data.decorations);
        this.decorationProofs = orderedDecorationProofsOf(data.decorationProofs);
        this.decorationCharges = decorationChargesOf(data.decorationCharges);
        this.decorationGroupId = decorationGroupIdOf(data);
        this.decorationGroup = decorationGroupOf(data.decorationGroup);
        this.decorationGroupFolded = isDecorationGroupFolded(data);

        Object.freeze(this);
    }
}

export default OrderedItem;

/**
 * A shopper-visible configuration choice made on an ordered item.
 *
 * @typedef {Object} OrderedItem~configDetail
 * @property {string} label - The label for this choice, e.g. "Paper".
 * @property {string} value - The option the shopper chose, e.g. "100# Gloss Text".
 */

/**
 * A reason a proof was denied.
 *
 * @typedef {Object} OrderedItem~denialReason
 * @property {number} id - The reason's unique id.
 * @property {string} name - The reason.
 */

/**
 * A stored artwork file attached to an ordered item.
 *
 * @typedef {Object} OrderedItem~artworkFile
 * @property {number} id - The file's unique id.
 * @property {string} idWithPrefix - The file's id, prefixed with its type.
 * @property {string} name - The file name.
 * @property {string} link - A link to the file.
 * @property {string} downloadUrl - The url to download the file from.
 */
